import { z } from 'zod';

/**
 * Staff roles inside a shop (tenant). Identity in this product is the email address;
 * the role decides what that identity may do. Owner > manager > tailor.
 */
export const USER_ROLES = ['owner', 'manager', 'tailor'] as const;

export type UserRole = (typeof USER_ROLES)[number];

export const userRoleSchema = z.enum(USER_ROLES);

/**
 * Higher rank means broader authority. Used for "at least this role" checks so a
 * permission test never has to enumerate roles.
 */
export const USER_ROLE_RANK: Readonly<Record<UserRole, number>> = Object.freeze({
  owner: 30,
  manager: 20,
  tailor: 10,
});

/** Default English labels. The UI must resolve `role.<key>` through i18next, not use these directly. */
export const USER_ROLE_LABELS: Readonly<Record<UserRole, string>> = Object.freeze({
  owner: 'Owner',
  manager: 'Manager',
  tailor: 'Tailor',
});

/** Roles that log in with a password. Tailors use a 4-digit PIN instead. */
export const PASSWORD_LOGIN_ROLES: readonly UserRole[] = Object.freeze(['owner', 'manager']);

/** Roles that log in with a 4-digit PIN. */
export const PIN_LOGIN_ROLES: readonly UserRole[] = Object.freeze(['tailor']);

export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as readonly string[]).includes(value);
}

/** True when `role` has at least the authority of `minimum`. */
export function roleAtLeast(role: UserRole, minimum: UserRole): boolean {
  return USER_ROLE_RANK[role] >= USER_ROLE_RANK[minimum];
}
